import { useMemo, useState } from "react";
import { Ban, CheckCircle2, Copy, KeyRound, Pencil, Plus, Users, UserX } from "lucide-react";

import { Button } from "components/ui/Button";
import { ConfirmDialog } from "components/ui/ConfirmDialog";
import { DataTable } from "components/ui/DataTable";
import { Pill, PILL_COLORS } from "components/ui/Pill";
import { StatsRow } from "features/reports/components/ReportInsights/InsightsLayout";
import { StatCard } from "features/reports/components/ReportInsights/StatCard";

import pageStyles from "../../UserDetailsPage.module.css";
import { formatRole } from "../../utils/formatRole";
import styles from "./UsersTable.module.css";

const ROLE_PILL_COLORS = {
  ADMIN: PILL_COLORS.docSystemGenerated,
  DOCTOR: PILL_COLORS.statusSuccess,
  NURSE: PILL_COLORS.statusPending,
  USER: PILL_COLORS.method,
};

const rolePillColors = (role) =>
  ROLE_PILL_COLORS[(role ?? "").toUpperCase()] ?? PILL_COLORS.statusNoStatus;

const fullName = (user) =>
  `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() || "-";

/**
 * Staff list for the "All users" tab. Search, paging and the empty state come
 * from DataTable; access is changed through a confirmed row action rather than
 * a switch, so the destructive half is deliberate.
 */
export const UsersTable = ({
  tabs,
  users,
  isLoading,
  search,
  onSearchChange,
  onAddUser,
  onEditUser,
  onToggleAccess,
  onCopyEmail,
  stats,
  isStatsLoading,
  disabledUsersCount,
}) => {
  const [userToToggle, setUserToToggle] = useState(null);
  const [isToggling, setIsToggling] = useState(false);

  const columns = useMemo(
    () => [
      {
        id: "name",
        header: "Name",
        width: "minmax(160px, 2fr)",
        render: fullName,
      },
      {
        id: "email",
        header: "Email",
        width: "minmax(180px, 2fr)",
        getTooltipText: (user) => user.email || "-",
        render: (user) => (
          <span className={styles.emailCell}>
            <span className={styles.email}>{user.email || "-"}</span>
            {!!user.email && (
              <button
                type="button"
                className={styles.copyButton}
                title="Copy email to clipboard"
                aria-label={`Copy ${user.email}`}
                onClick={(event) => {
                  event.stopPropagation();
                  onCopyEmail(user.email);
                }}
              >
                <Copy size={14} />
              </button>
            )}
          </span>
        ),
      },
      // The three identifiers a practice is asked for by name. They were on the profile only, so
      // checking whether a doctor had a GMS number meant opening every record one at a time (KIN-1935).
      {
        id: "professionalMedicalNumber",
        header: "MCN",
        width: "110px",
        render: (user) => user.professionalMedicalNumber || "-",
      },
      {
        id: "gmsNumber",
        header: "GMS Contract No.",
        width: "140px",
        render: (user) => user.gmsNumber || "-",
      },
      {
        id: "deaspPanelNumber",
        header: "DEASP No.",
        width: "120px",
        render: (user) => user.deaspPanelNumber || "-",
      },
      {
        id: "mainRole",
        header: "Role",
        width: "130px",
        wrap: true,
        render: (user) => (
          <Pill {...rolePillColors(user.mainRole)}>
            {formatRole(user.mainRole)}
          </Pill>
        ),
      },
      {
        id: "roster",
        header: "Roster",
        width: "100px",
        wrap: true,
        render: (user) => (
          <Pill
            {...(user.roster
              ? PILL_COLORS.statusSuccess
              : PILL_COLORS.statusNoStatus)}
          >
            {user.roster ? "Yes" : "No"}
          </Pill>
        ),
      },
      {
        id: "access",
        header: "Access",
        width: "120px",
        wrap: true,
        render: (user) => (
          <Pill
            {...(user.disabled
              ? PILL_COLORS.statusError
              : PILL_COLORS.statusSuccess)}
          >
            {user.disabled ? "Disabled" : "Enabled"}
          </Pill>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onCopyEmail]
  );

  const rowActions = useMemo(
    () => [
      {
        label: "Edit user",
        icon: Pencil,
        primary: true,
        onClick: (user) => onEditUser(user.id),
      },
      {
        label: "Enable user",
        icon: CheckCircle2,
        primary: true,
        visibleWhen: (user) => !!user.disabled,
        onClick: (user) => setUserToToggle(user),
      },
      {
        label: "Disable user",
        icon: Ban,
        primary: true,
        visibleWhen: (user) => !user.disabled,
        onClick: (user) => setUserToToggle(user),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onEditUser]
  );

  const isDisabling = !userToToggle?.disabled;

  return (
    <div className={pageStyles.section}>
      <div className={pageStyles.sectionHeader}>
        <h3 className={pageStyles.sectionTitle}>All Users</h3>
        <Button variant="text" endIcon={Plus} onClick={onAddUser}>
          Add new
        </Button>
      </div>

      <div className={styles.statsSection}>
        <StatsRow>
          <StatCard
            label="Licences"
            icon={KeyRound}
            isLoading={isStatsLoading}
            value={stats?.licencedUsers ?? "-"}
          />
          <StatCard
            label="Active Users"
            icon={Users}
            isLoading={isStatsLoading}
            value={stats?.activeUsers ?? "-"}
          />
          <StatCard
            label="Disabled Users"
            icon={UserX}
            isLoading={isLoading}
            value={disabledUsersCount}
          />
        </StatsRow>
      </div>

      {tabs}

      <DataTable
        columns={columns}
        data={users}
        /* Size the grid to the card rather than to its content (KIN-1935). The default is
           `width: max-content`, under which the `fr` shares resolve against the content width and
           the `minmax` floors never bind — so the three identifier columns pushed the table past
           the card and into horizontal scroll, and no amount of lowering those floors moved it.
           At `width: 100%` the shares divide the space that exists and a long name truncates.
           Same reason VaccinesTable carries it (KIN-1894). */
        noScroll
        getRowId={(row) => row.id}
        isLoading={isLoading}
        emptyTitle="No users found"
        emptyText={
          search
            ? "No user matches that name."
            : "Use Add new and they will appear here."
        }
        loadingTitle="Loading users..."
        loadingText="Please wait while we fetch the staff list."
        search={{
          value: search,
          onChange: onSearchChange,
          placeholder: "Search user by name...",
        }}
        rowActions={rowActions}
        rowActionsLabel="User actions"
        rowsPerPage={10}
        rowsOptions={[10, 20, 50, 100]}
      />

      <ConfirmDialog
        isOpen={!!userToToggle}
        onClose={() => setUserToToggle(null)}
        onConfirm={async () => {
          setIsToggling(true);
          try {
            await onToggleAccess(userToToggle.id);
            setUserToToggle(null);
          } finally {
            setIsToggling(false);
          }
        }}
        isLoading={isToggling}
        title={isDisabling ? "Disable user" : "Enable user"}
        message={
          userToToggle
            ? isDisabling
              ? `${fullName(userToToggle)} will lose access to Kinesin until they are enabled again.`
              : `${fullName(userToToggle)} will be able to sign in to Kinesin again.`
            : ""
        }
        confirmLabel={isDisabling ? "Disable" : "Enable"}
      />
    </div>
  );
};
